import { z } from "zod";

export const flowStates = ["draft", "active", "archived"] as const;
export const flowStateSchema = z.enum(flowStates);
export type FlowState = z.infer<typeof flowStateSchema>;

export const flowReadinessStates = ["ready", "not-ready"] as const;
export const flowReadinessSchema = z.enum(flowReadinessStates);
export type FlowReadiness = z.infer<typeof flowReadinessSchema>;

export const flowSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  name: z.string().min(1),
  state: flowStateSchema,
  currentVersion: z.number().int().min(1),
  stepCount: z.number().int().min(0),
  gateCount: z.number().int().min(0),
  readiness: flowReadinessSchema,
  lastRunId: z.uuid().nullable(),
  updatedAt: z.iso.datetime(),
});
export type Flow = z.infer<typeof flowSchema>;

export const flowListSchema = z.array(flowSchema);
export type FlowList = z.infer<typeof flowListSchema>;

export const createFlowSchema = z.object({
  name: z.string().trim().min(1).max(120),
});
export type CreateFlow = z.infer<typeof createFlowSchema>;

export const flowStepSchema = z.object({
  name: z.string().min(1),
  kind: z.string().min(1),
  gate: z.boolean(),
  /**
   * The fields below are what a run follows. They are optional so a version published before the
   * engine existed still reads; a step with no recipe fails its run with a clear message.
   */
  id: z.string().min(1).optional(),
  recipe: z.string().min(1).optional(),
  rejectRoute: z.string().nullable().optional(),
  expiryMinutes: z.number().int().min(1).nullable().optional(),
});
export type FlowStep = z.infer<typeof flowStepSchema>;

export const publishFlowVersionSchema = z.object({
  steps: z.array(flowStepSchema).min(1),
  workflowCap: z.number().int().min(1).nullable().optional(),
});
export type PublishFlowVersion = z.infer<typeof publishFlowVersionSchema>;

export const flowVersionSchema = z.object({
  id: z.uuid(),
  flowId: z.uuid(),
  number: z.number().int().min(1),
  steps: z.array(flowStepSchema),
  workflowCap: z.number().int().min(1).nullable().optional(),
  readiness: flowReadinessSchema,
  /** The approval that confirmed this version, when it deploys. Null for a version that needed none. */
  approvalId: z.uuid().nullable().optional(),
  createdAt: z.iso.datetime(),
});
export type FlowVersion = z.infer<typeof flowVersionSchema>;

export const flowVersionListSchema = z.array(flowVersionSchema);
export type FlowVersionList = z.infer<typeof flowVersionListSchema>;
